import { useEffect, useMemo, useState } from "react";
import { Modal, Button, ProfilePaymentIcons } from "@components";
import { assignPaymentProfileToCustomers } from "@api";
import { toast } from "@components/toast/Toast";

const ChangePaymentProfileModal = ({
  isOpen,
  onClose,
  customers,
  paymentProfiles,
  onComplete,
}) => {
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setSelectedId(null);
  }, [isOpen]);

  const total = customers?.length ?? 0;

  const countsByProfile = useMemo(() => {
    const counts = {};
    (customers ?? []).forEach((c) => {
      if (c.paymentProfileId) {
        counts[c.paymentProfileId] = (counts[c.paymentProfileId] ?? 0) + 1;
      }
    });
    return counts;
  }, [customers]);

  const alreadyApplied = selectedId ? (countsByProfile[selectedId] ?? 0) : 0;
  const applyCount = total - alreadyApplied;

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const ids = (customers ?? [])
        .filter((c) => c.paymentProfileId !== selectedId)
        .map((c) => c.id);
      const profileName = paymentProfiles.find((p) => p.id === selectedId)?.name;
      if (ids.length > 0) {
        // Controller swallows errors and returns null on failure (legacy
        // contract). Bail out without firing onComplete so the modal stays
        // open and the success toast doesn't lie about the outcome.
        const result = await assignPaymentProfileToCustomers(selectedId, ids);
        if (result == null) {
          toast({
            description: `Failed to assign "${profileName}" Payment profile.`,
          });
          return;
        }
        toast({
          description: `"${profileName}" Payment profile assigned to ${applyCount} ${applyCount === 1 ? "account" : "accounts"}`,
        });
      }
      if (onComplete) onComplete();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span>
          Change Payment Profile{" "}
          <span className="font-normal text-xs">
            ({total} {total === 1 ? "account" : "accounts"})
          </span>
        </span>
      }
      size="sm"
      preventBackdropClose
    >
      <div className="p-5">
        <div
          role="radiogroup"
          aria-label="Payment profiles"
          className="flex flex-col gap-2 max-h-72 overflow-y-auto"
        >
          {paymentProfiles.map((profile) => {
            const isSelected = selectedId === profile.id;
            const count = countsByProfile[profile.id] ?? 0;
            return (
              <button
                key={profile.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => setSelectedId(profile.id)}
                className={`flex flex-col items-start text-left rounded-lg px-4 py-3 text-sm cursor-pointer border focus:outline-none ${
                  isSelected
                    ? "border-primary"
                    : "border-zinc-200 hover:bg-zinc-50"
                }`}
              >
                <span className="flex items-center gap-2 font-semibold text-zinc-900">
                  {profile.name}
                  <ProfilePaymentIcons profile={profile} size="sm" />
                </span>
                {isSelected && count > 0 && (
                  <span className="mt-1 text-xs text-zinc-600">
                    {count} / {total} accounts selected already have this
                    profile applied
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-zinc-200 p-4">
        <Button
          type="button"
          className="rounded-lg"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          type="button"
          color="primary"
          className="rounded-lg"
          onClick={handleSave}
          disabled={!selectedId || applyCount === 0}
          submitting={saving}
        >
          Apply to {applyCount} {applyCount === 1 ? "account" : "accounts"}
        </Button>
      </div>
    </Modal>
  );
};

export default ChangePaymentProfileModal;
